import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { useNavigate, useParams } from "react-router-dom"
import { Formik, Form } from "formik"
import * as yup from "yup"
import { Bounce, toast } from "react-toastify"
import { Modal, ModalBody } from "reactstrap"
import PageHeader from "../../../components/DashboardComponents/PageHeader"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomInput from "../../../components/OnboardingComponents/CustomInput"
import PasswordRequirementsHint from "../../../components/OnboardingComponents/PasswordRequirementsHint"
import PreferredCitySelect from "../../../components/Common/PreferredCitySelect"
import { ICONS } from "../../../constants/icons"
import { get, post } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import { formatDisplayName, getUserFullName } from "../../../helpers/userDisplay"
import { reportFormApiError } from "../../../helpers/formFieldErrors"
import {
  PASSWORD_COMPLEXITY,
  PASSWORD_REQUIREMENTS,
  getPasswordFieldError,
  getPasswordHelperTone,
} from "../../../helpers/passwordRules"
import ProfilePhoto from "../../Profile/ProfilePhoto"
import {
  mapAdminMemberDetail,
  mapAdminMemberEditFormValues,
  mapAdminMemberEditLockedCities,
} from "./adminMembersData"

const PROFILE_FORM_ID = "admin-edit-member-form"
const MAX_PHOTO_BYTES = 800 * 1024

const ADDRESS_SLOTS = [
  { key: "primary", field: "primaryCityId", label: "Primary City" },
  { key: "secondary", field: "secondaryCityId", label: "Secondary City" },
  { key: "tertiary", field: "tertiaryCityId", label: "Tertiary City" },
]

const buildExcludeCityIdsForSlot = (slotKey, values) =>
  ADDRESS_SLOTS.filter(({ key }) => key !== slotKey)
    .map(({ field }) => values[field])
    .filter(Boolean)
    .map(String)

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const passwordInitialValues = {
  password: "",
  passwordConfirmation: "",
}

const passwordValidationSchema = yup.object().shape({
  password: yup
    .string()
    .required("New password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(PASSWORD_COMPLEXITY, PASSWORD_REQUIREMENTS),
  passwordConfirmation: yup
    .string()
    .required("Please confirm the new password")
    .oneOf([yup.ref("password")], "Passwords must match"),
})

const ChangePasswordModal = ({ isOpen, onClose, memberId }) => {
  const handlePasswordSubmit = async (values, { setSubmitting, resetForm }) => {
    if (!memberId) {
      toast.error("Member id is missing.", TOAST_OPTIONS)
      setSubmitting(false)
      return
    }
    try {
      const response = await post(
        endpoints.updateAdminMemberPassword(memberId),
        {
          password: values.password,
          password_confirmation: values.passwordConfirmation,
        },
        { skipErrorToast: true },
      )
      if (
        !response ||
        response.status >= 400 ||
        response?.data?.success === false
      ) {
        toastApiError(response, "Failed to update password")
        return
      }
      toast.success(
        response?.data?.message || "Password updated successfully.",
        TOAST_OPTIONS,
      )
      resetForm()
      onClose()
    } catch (err) {
      toastApiError(err, "Failed to update password")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      className="amok-modal amok-modal--password"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body edit-profile__modal-body">
        <Formik
          key={isOpen ? "password-open" : "password-closed"}
          initialValues={passwordInitialValues}
          validationSchema={passwordValidationSchema}
          validateOnChange
          validateOnBlur
          onSubmit={handlePasswordSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldTouched,
            isSubmitting,
          }) => (
              <Form className="edit-profile__modal-form">
                <div className="edit-profile__modal-main">
                  <h3 className="edit-profile__modal-title">Change Password</h3>
                  <div className="edit-profile__modal-fields">
                    <div className="edit-profile__password-field">
                      <CustomInput
                        label="Create New Password"
                        type="password"
                        name="password"
                        placeholder="**********"
                        value={values.password}
                        onChange={event => {
                          handleChange(event)
                          setFieldTouched("password", true, false)
                        }}
                        onBlur={() => setFieldTouched("password", true)}
                        error={getPasswordFieldError(
                          touched.password,
                          errors.password,
                        )}
                        helperTone={getPasswordHelperTone(values.password)}
                        autoComplete="new-password"
                      />
                      <PasswordRequirementsHint value={values.password} />
                    </div>
                    <CustomInput
                      label="Confirm New Password"
                      type="password"
                      name="passwordConfirmation"
                      placeholder="**********"
                      value={values.passwordConfirmation}
                      onChange={handleChange}
                      onBlur={() =>
                        setFieldTouched("passwordConfirmation", true)
                      }
                      error={
                        touched.passwordConfirmation &&
                        errors.passwordConfirmation
                      }
                      autoComplete="new-password"
                    />
                  </div>
                </div>
                <div className="edit-profile__modal-actions">
                  <CustomButton
                    variant="danger"
                    size="md"
                    type="button"
                    className="edit-profile__modal-cancel"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </CustomButton>
                  <CustomButton
                    variant="primary"
                    size="md"
                    type="submit"
                    className="edit-profile__modal-submit"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    Update Password
                  </CustomButton>
                </div>
              </Form>
            )
          }
        </Formik>
      </ModalBody>
    </Modal>
  )
}

ChangePasswordModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  memberId: PropTypes.string,
}

ChangePasswordModal.defaultProps = {
  memberId: undefined,
}

const isValidPhoneNumber = value => {
  const raw = String(value || "").trim()
  if (!raw) return true
  if (!/^\+?[\d\s().-]+$/.test(raw)) return false
  const digits = raw.replace(/\D/g, "")
  return digits.length >= 7 && digits.length <= 15
}

const profileInitialValues = {
  firstName: "",
  lastName: "",
  email: "",
  phone_number: "",
  listeningTo: "",
  reading: "",
  primaryCityId: "",
  secondaryCityId: "",
  tertiaryCityId: "",
}

const profileValidationSchema = yup.object().shape({
  firstName: yup
    .string()
    .trim()
    .required("First name is required")
    .min(2, "First name must be at least 2 characters"),
  lastName: yup.string().trim(),
  phone_number: yup
    .string()
    .trim()
    .test(
      "phone-format",
      "Enter a valid phone number (7–15 digits)",
      isValidPhoneNumber,
    ),
  listeningTo: yup.string().trim(),
  reading: yup.string().trim(),
  primaryCityId: yup.string().trim(),
  secondaryCityId: yup.string().trim(),
  tertiaryCityId: yup.string().trim(),
})

const AdminMemberEdit = () => {
  const navigate = useNavigate()
  const { profileId, cityId } = useParams()
  const memberKey = profileId ? decodeURIComponent(profileId) : ""
  const memberDetailPath = cityId
    ? `/admin/cities/${cityId}/members/${encodeURIComponent(memberKey)}`
    : `/admin/members/${encodeURIComponent(memberKey)}`
  const fileInputRef = useRef(null)
  const [isPasswordOpen, setIsPasswordOpen] = useState(false)
  const [photoPreview, setPhotoPreview] = useState(null)
  const [profileImageFile, setProfileImageFile] = useState(null)
  const [lockedCities, setLockedCities] = useState({
    primary: false,
    secondary: false,
    tertiary: false,
  })
  const [lockedCityLabels, setLockedCityLabels] = useState({
    primary: "",
    secondary: "",
    tertiary: "",
  })
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [memberId, setMemberId] = useState("")
  const [formInitialValues, setFormInitialValues] = useState(
    profileInitialValues,
  )

  useEffect(() => {
    document.title = "Edit Profile | AMOK . EARTH Admin"
  }, [])

  useEffect(() => {
    let cancelled = false

    const loadProfile = async () => {
      if (!memberKey) {
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        const response = await get(endpoints.getAdminMemberByEmail(memberKey))
        if (cancelled) return
        if (response?.data?.success) {
          const result = response.data.result || {}
          const mapped = mapAdminMemberDetail(result)
          const { locked, labels } = mapAdminMemberEditLockedCities(result)
          setMemberId(mapped.id || "")
          setFormInitialValues(mapAdminMemberEditFormValues(result))
          setLockedCities(locked)
          setLockedCityLabels(labels)
          setPhotoPreview(
            mapped.profile_media?.thumbnail ||
              mapped.profile_media?.image ||
              null,
          )
        } else {
          toast.error("Failed to load member profile.", TOAST_OPTIONS)
        }
      } catch {
        if (!cancelled) {
          toast.error("Failed to load member profile.", TOAST_OPTIONS)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadProfile()
    return () => {
      cancelled = true
    }
  }, [memberKey])

  const handlePhotoChange = e => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > MAX_PHOTO_BYTES) {
      toast.error("Image must be 800K or smaller.", TOAST_OPTIONS)
      e.target.value = ""
      return
    }

    setProfileImageFile(file)
    setPhotoPreview(prev => {
      if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev)
      return URL.createObjectURL(file)
    })
  }

  const handleSubmit = async (values, { setFieldError, setFieldTouched }) => {
    if (saving) return
    if (!memberId) {
      toast.error("Member id is missing.", TOAST_OPTIONS)
      return
    }
    setSaving(true)

    try {
      const formData = new FormData()
      formData.append("member_id", memberId)
      formData.append(
        "first_name",
        formatDisplayName(values.firstName.trim()),
      )
      formData.append(
        "last_name",
        formatDisplayName(values.lastName?.trim() || ""),
      )
      formData.append("listening_to", values.listeningTo?.trim() || "")
      formData.append("reading", values.reading?.trim() || "")
      if (values.phone_number?.trim()) {
        formData.append("phone_number", values.phone_number.trim())
      }
      if (profileImageFile) {
        formData.append("profile_image", profileImageFile)
      }

      ADDRESS_SLOTS.forEach(({ key, field }) => {
        if (lockedCities[key]) return
        const cityIdValue = String(values[field] || "").trim()
        if (cityIdValue) {
          formData.append(`addresses[${key}]`, cityIdValue)
        }
      })

      const response = await post(endpoints.updateBasicProfile, formData, {
        skipErrorToast: true,
      })

      if (response?.data?.success) {
        toast.success(
          response.data.message || "Profile updated successfully.",
          TOAST_OPTIONS,
        )
        navigate(memberDetailPath)
      } else if (response) {
        reportFormApiError(response, "Failed to update profile", {
          setFieldError,
          setFieldTouched,
        }, {
          first_name: "firstName",
          last_name: "lastName",
          phone_number: "phone_number",
        })
      }
    } catch (err) {
      reportFormApiError(err, "Failed to update profile", {
        setFieldError,
        setFieldTouched,
      }, {
        first_name: "firstName",
        last_name: "lastName",
        phone_number: "phone_number",
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="admin-page admin-member-edit">
      <div className="edit-profile edit-profile--admin" data-profile-id={profileId}>
        <Formik
          initialValues={formInitialValues}
          validationSchema={profileValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
          validateOnBlur
          validateOnChange
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldTouched,
            setFieldValue,
            isSubmitting,
          }) => (
            <>
              <PageHeader
                className="edit-profile__header"
                title="Edit Profile"
                subtitle="Update your profile."
                actions={
                  <>
                    <CustomButton
                      variant="secondary"
                      size="md"
                      type="button"
                      className="edit-profile__btn-password"
                      onClick={() => setIsPasswordOpen(true)}
                    >
                      Change Password
                    </CustomButton>
                    <CustomButton
                      variant="primary"
                      size="md"
                      type="submit"
                      form={PROFILE_FORM_ID}
                      trailingIconSrc={ICONS.arrowRight}
                      trailingIconAlt=""
                      loading={saving || isSubmitting || loading}
                      disabled={saving || isSubmitting || loading}
                    >
                      Save Changes
                    </CustomButton>
                  </>
                }
              />

              <Form id={PROFILE_FORM_ID} className="edit-profile__content">
                <section className="edit-profile__section">
                  <div className="edit-profile__section-head">
                    <h2 className="edit-profile__section-title">
                      Profile Photo
                    </h2>
                  </div>
                  <div className="edit-profile__photo-row">
                    <ProfilePhoto
                      src={photoPreview}
                      name={getUserFullName({
                        first_name: formInitialValues.firstName,
                        last_name: formInitialValues.lastName,
                      })}
                      loading={loading && !photoPreview}
                      className="edit-profile__photo"
                    />
                    <div className="edit-profile__photo-actions">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif"
                        className="edit-profile__file-input"
                        onChange={handlePhotoChange}
                      />
                      <CustomButton
                        variant="ghost"
                        size="sm"
                        type="button"
                        className="edit-profile__upload-btn"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        Upload Photo
                      </CustomButton>
                      <p className="edit-profile__photo-hint">
                        JPG, GIF or PNG. Max size of 800K
                      </p>
                    </div>
                  </div>
                </section>

                <section className="edit-profile__section edit-profile__section--info">
                  <div className="edit-profile__section-head">
                    <h2 className="edit-profile__section-title">
                      Personal Information
                    </h2>
                  </div>

                  <div className="edit-profile__form">
                    <div className="edit-profile__form-row">
                      <CustomInput
                        label="First Name"
                        name="firstName"
                        placeholder="Enter first name"
                        value={values.firstName}
                        onChange={handleChange}
                        onBlur={() => setFieldTouched("firstName", true)}
                        error={touched.firstName && errors.firstName}
                        autoComplete="given-name"
                      />
                      <CustomInput
                        label="Last Name"
                        name="lastName"
                        placeholder="Enter last name"
                        value={values.lastName}
                        onChange={handleChange}
                        onBlur={() => setFieldTouched("lastName", true)}
                        error={touched.lastName && errors.lastName}
                        autoComplete="family-name"
                      />
                    </div>

                    <div className="edit-profile__form-row">
                      <CustomInput
                        label="Email Address"
                        type="email"
                        name="email"
                        placeholder="Enter email address"
                        value={values.email}
                        disabled
                        readOnly
                        autoComplete="email"
                      />
                      <CustomInput
                        label="Phone Number"
                        type="tel"
                        name="phone_number"
                        placeholder="Enter phone number"
                        value={values.phone_number}
                        onChange={handleChange}
                        onBlur={() => setFieldTouched("phone_number", true)}
                        error={touched.phone_number && errors.phone_number}
                        autoComplete="tel"
                      />
                    </div>

                    <div className="edit-profile__form-row edit-profile__form-row--3">
                      {ADDRESS_SLOTS.map(({ key, field, label }) =>
                        lockedCities[key] ? (
                          <CustomInput
                            key={field}
                            label={label}
                            name={field}
                            placeholder="City, State"
                            value={lockedCityLabels[key] || ""}
                            disabled
                            readOnly
                          />
                        ) : (
                          <PreferredCitySelect
                            key={field}
                            label={label}
                            loadFromApi
                            placeholder="Select a city"
                            excludeIds={buildExcludeCityIdsForSlot(key, values)}
                            value={values[field]}
                            onChange={value => setFieldValue(field, value)}
                            onBlur={() => setFieldTouched(field, true)}
                            error={touched[field] && errors[field]}
                          />
                        ),
                      )}
                    </div>

                    <CustomInput
                      label="Listening to"
                      name="listeningTo"
                      textarea
                      rows={3}
                      placeholder="What are you listening to? (optional)"
                      value={values.listeningTo}
                      onChange={handleChange}
                      onBlur={() => setFieldTouched("listeningTo", true)}
                      error={touched.listeningTo && errors.listeningTo}
                    />

                    <CustomInput
                      label="Reading"
                      name="reading"
                      textarea
                      rows={3}
                      placeholder="What are you reading? (optional)"
                      value={values.reading}
                      onChange={handleChange}
                      onBlur={() => setFieldTouched("reading", true)}
                      error={touched.reading && errors.reading}
                    />
                  </div>
                </section>
              </Form>
            </>
          )}
        </Formik>

        <ChangePasswordModal
          isOpen={isPasswordOpen}
          onClose={() => setIsPasswordOpen(false)}
          memberId={memberId}
        />
      </div>
    </div>
  )
}

export default AdminMemberEdit
